import { Timestamp } from 'firebase/firestore';

/**
 * Mérőállás Kezelési Rendszer - TypeScript Típusok
 * A dokumentáció (1227.txt) alapján
 */

// Energia típusok
export type EnergyType = 'electricity' | 'gas';

// Leolvasás kódok
export enum ReadingCode {
  DISTRIBUTOR_ACTUAL = '01',      // Elosztó általi leolvasás (tényleges)
  TRADER_CUSTOMER = '02',          // Kereskedő/ügyfél diktálás
  MOVE_OUT = '03',                 // Kiköltözéskori
  MOVE_IN = '06',                  // Beköltözéskori
  VERIFICATION = '10',             // Ellenőrző mérés (nem számlázza)
  METER_REMOVAL = '21',            // Mérő leszerelés
  METER_INSTALLATION = '22',       // Mérő beszerelés
  DISTRIBUTOR_ESTIMATE = '95'      // Elosztói becslés
}

// Elszámolás státuszkódok
export enum SettlementStatus {
  BASE = '01',                     // Alap státusz
  NEEDS_VERIFICATION = '02',       // Ellenőrzés szükséges
  APPROVED = '04',                 // Mérőállás jóváhagyva
  READY_FOR_BILLING = '05',        // Számlázásra átadható
  SETTLED = '07',                  // Elszámolva
  NOT_SETTLEABLE = '10'            // Nem elszámolandó
}

// Leolvasás kód leírások
export const ReadingCodeLabels: Record<ReadingCode, string> = {
  [ReadingCode.DISTRIBUTOR_ACTUAL]: 'Elosztó általi leolvasás (tényleges)',
  [ReadingCode.TRADER_CUSTOMER]: 'Kereskedő/ügyfél diktálás',
  [ReadingCode.MOVE_OUT]: 'Kiköltözéskori',
  [ReadingCode.MOVE_IN]: 'Beköltözéskori',
  [ReadingCode.VERIFICATION]: 'Ellenőrző mérés',
  [ReadingCode.METER_REMOVAL]: 'Mérő leszerelés',
  [ReadingCode.METER_INSTALLATION]: 'Mérő beszerelés',
  [ReadingCode.DISTRIBUTOR_ESTIMATE]: 'Elosztói becslés'
};

// Elszámolás státusz leírások
export const SettlementStatusLabels: Record<SettlementStatus, string> = {
  [SettlementStatus.BASE]: 'Alap státusz',
  [SettlementStatus.NEEDS_VERIFICATION]: 'Ellenőrzés szükséges',
  [SettlementStatus.APPROVED]: 'Mérőállás jóváhagyva',
  [SettlementStatus.READY_FOR_BILLING]: 'Számlázásra átadható',
  [SettlementStatus.SETTLED]: 'Elszámolva',
  [SettlementStatus.NOT_SETTLEABLE]: 'Nem elszámolandó'
};

// Mérőállás interface
export interface MeterReading {
  id: string;
  meterPointId?: string;
  customerId?: string;
  podId: string;
  serialNumber: string;
  readingDate: Timestamp | Date;
  readingValue: number;
  readingCode: ReadingCode;
  settlementStatus: SettlementStatus;
  source: 'manual' | 'mscons' | 'utilmd' | 'excel_import';
  energyType: EnergyType;
  consumption?: number;
  validated: boolean;
  validationMessage?: string;
  needsReview: boolean;
  notes?: string;
  createdAt: Timestamp | Date;
  createdBy: string;
  modifiedAt?: Timestamp | Date;
  modifiedBy?: string;
}

// Mérőhely interface
export interface MeterPoint {
  id: string;
  customerId: string;
  consumptionLocationId?: string;
  podId: string;
  serialNumber: string;
  energyType: EnergyType;
  multiplier: number;
  provider: string;
  installationDate?: Timestamp | Date;
  removalDate?: Timestamp | Date;
  isActive: boolean;
  isBillable: boolean;
  billableFrom?: Timestamp | Date;
  billableTo?: Timestamp | Date;
  notes?: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Fogyasztási hely cím interface
export interface LocationAddress {
  postalCode: string;      // Irányítószám
  city: string;            // Helység/Város
  street: string;          // Utca
  houseNumber: string;     // Házszám (pl. "12", "12/A", "12-14")
  building?: string;       // Épület (opcionális)
  floor?: string;          // Emelet (opcionális)
  door?: string;           // Ajtó (opcionális)
  notes?: string;          // Megjegyzés (pl. "Hátsó bejárat", "2. udvar")
}

// Fogyasztási hely interface
export interface ConsumptionLocation {
  id: string;
  customerId: string;
  locationName: string;
  address: LocationAddress;  // Strukturált cím
  fullAddress?: string;      // Generált teljes cím (kereséshez)
  locationType: 'residential' | 'commercial' | 'industrial';
  isActive: boolean;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Mérőállás import eredmény
export interface MeterReadingImportResult {
  total: number;
  success: number;
  failed: number;
  validated: number;
  needsReview: number;
  errors?: Array<{
    row: number;
    message: string;
  }>;
}

// Mérőállás szűrő
export interface MeterReadingFilter {
  customerId?: string;
  podId?: string;
  energyType?: EnergyType;
  settlementStatus?: SettlementStatus;
  needsReview?: boolean;
  startDate?: Date;
  endDate?: Date;
}

// Mérőállás összesítő
export interface MeterReadingSummary {
  totalReadings: number;
  validated: number;
  needsReview: number;
  byEnergyType: Record<EnergyType, number>;
  byStatus: Record<SettlementStatus, number>;
  uniquePods: number;
  pods: string[];
}

// Form adatok mérőállás rögzítéshez
export interface MeterReadingFormData {
  podId: string;
  serialNumber: string;
  readingDate: Date;
  readingValue: number;
  readingCode: ReadingCode;
  energyType: EnergyType;
  notes?: string;
}

// Státusz váltás validáció
export const AllowedStatusTransitions: Record<SettlementStatus, SettlementStatus[]> = {
  [SettlementStatus.BASE]: [SettlementStatus.NEEDS_VERIFICATION, SettlementStatus.APPROVED],
  [SettlementStatus.NEEDS_VERIFICATION]: [SettlementStatus.BASE, SettlementStatus.APPROVED],
  [SettlementStatus.APPROVED]: [SettlementStatus.READY_FOR_BILLING],
  [SettlementStatus.READY_FOR_BILLING]: [SettlementStatus.SETTLED],
  [SettlementStatus.SETTLED]: [],
  [SettlementStatus.NOT_SETTLEABLE]: []
};
